import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Payment as PaymentIcon,
  Download as DownloadIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import {
  formatCents,
  getTenantLedger,
  recordManualPayment,
  voidPayment,
  collectDeposit,
  applyDeposit,
  refundDeposit,
  downloadReceiptPdf,
  exportFinancialsCsv,
  heldDepositCents
} from './api';
import { PAYMENT_METHOD_TYPES } from './constants';

const METHOD_OPTIONS = PAYMENT_METHOD_TYPES.filter(
  (m) => !String(m).startsWith('stripe')
);

/**
 * Admin tenant ledger view (ZRF-FIN-006).
 */
function TenantLedgerView({
  tenantId,
  propertyId,
  unitId: initialUnitId,
  leaseId: initialLeaseId,
  tenantLabel,
  onBack
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [depositDialog, setDepositDialog] = useState(null); // { deposit, action }
  const [voidDialog, setVoidDialog] = useState(null); // { paymentId }
  const [voidReason, setVoidReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [payForm, setPayForm] = useState({
    amountDollars: '',
    paymentDate: new Date().toLocaleDateString('en-CA'),
    methodType: 'interac_etransfer',
    referenceNumber: '',
    notes: ''
  });
  const [depositAmount, setDepositAmount] = useState('');

  const load = useCallback(async () => {
    if (!tenantId || !propertyId) {
      setError('tenantId and propertyId are required');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const ledger = await getTenantLedger(tenantId, propertyId, {
        unitId: initialUnitId || undefined,
        leaseId: initialLeaseId || undefined
      });
      setData(ledger);
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || 'Failed to load ledger';
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId, propertyId, initialUnitId, initialLeaseId]);

  useEffect(() => {
    load();
  }, [load]);

  const balance = data?.balance || {};
  const unitId =
    initialUnitId ||
    data?.entries?.[0]?.unitId ||
    data?.schedules?.[0]?.unitId ||
    data?.deposits?.[0]?.unitId ||
    '';
  const leaseId =
    initialLeaseId ||
    data?.schedules?.[0]?.leaseId ||
    data?.deposits?.[0]?.leaseId ||
    null;

  const handleSavePayment = async () => {
    setSubmitting(true);
    try {
      await recordManualPayment({
        tenantId,
        propertyId,
        unitId,
        leaseId: leaseId || undefined,
        amountDollars: payForm.amountDollars,
        paymentDate: payForm.paymentDate,
        methodType: payForm.methodType,
        referenceNumber: payForm.referenceNumber || undefined,
        notes: payForm.notes || undefined
      });
      setPayOpen(false);
      setSnackbar({ open: true, message: 'Payment recorded', severity: 'success' });
      await load();
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || 'Failed to record payment';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const openDepositAction = (deposit, action) => {
    const held = heldDepositCents(deposit);
    const remaining =
      Number(deposit.expectedAmountCents || 0) -
      Number(deposit.collectedAmountCents || 0);
    let defaultDollars = '';
    if (action === 'collect' && remaining > 0) {
      defaultDollars = (remaining / 100).toFixed(2);
    } else if ((action === 'apply' || action === 'refund') && held > 0) {
      defaultDollars = (held / 100).toFixed(2);
    }
    setDepositAmount(defaultDollars);
    setDepositDialog({ deposit, action });
  };

  const handleDepositAction = async () => {
    if (!depositDialog) return;
    const { deposit, action } = depositDialog;
    setSubmitting(true);
    try {
      const body = { amountDollars: depositAmount };
      if (action === 'collect') await collectDeposit(deposit.id, body);
      else if (action === 'apply') await applyDeposit(deposit.id, body);
      else if (action === 'refund') await refundDeposit(deposit.id, body);
      setDepositDialog(null);
      setSnackbar({
        open: true,
        message:
          action === 'collect'
            ? 'Deposit collected'
            : action === 'apply'
              ? 'Deposit applied to rent'
              : 'Deposit refunded',
        severity: 'success'
      });
      await load();
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || `Failed to ${action} deposit`;
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadReceipt = async (receiptId, receiptNumber) => {
    try {
      const blob = await downloadReceiptPdf(receiptId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${receiptNumber || receiptId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || 'Failed to download receipt';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const paymentStatusById = (data?.payments || []).reduce((acc, p) => {
    acc[p.id] = p.status;
    return acc;
  }, {});

  const openVoidPayment = (paymentId) => {
    setVoidReason('');
    setVoidDialog({ paymentId });
  };

  const handleVoidPayment = async () => {
    if (!voidDialog?.paymentId) return;
    setSubmitting(true);
    try {
      await voidPayment(voidDialog.paymentId, { reason: voidReason });
      setVoidDialog(null);
      setSnackbar({ open: true, message: 'Payment voided', severity: 'success' });
      await load();
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || 'Failed to void payment';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportCsv = async (type) => {
    try {
      await exportFinancialsCsv({
        type,
        tenantId,
        propertyId
      });
      setSnackbar({ open: true, message: 'Export downloaded', severity: 'success' });
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || 'Failed to export';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const depositActionLabel =
    depositDialog?.action === 'collect'
      ? 'Collect deposit'
      : depositDialog?.action === 'apply'
        ? 'Apply to rent'
        : depositDialog?.action === 'refund'
          ? 'Refund deposit'
          : '';

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        {onBack && (
          <Button startIcon={<ArrowBackIcon />} onClick={onBack} size="small">
            Back
          </Button>
        )}
        <Typography variant="h5" component="h1">
          Tenant Ledger{tenantLabel ? `: ${tenantLabel}` : ''}
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Alert severity="info" sx={{ flex: 1 }}>
              Balance due:{' '}
              <strong>
                {formatCents(balance.balanceDueCents, balance.currency)}
              </strong>
              {balance.creditBalanceCents > 0 && (
                <>
                  {' '}
                  · Credit:{' '}
                  {formatCents(balance.creditBalanceCents, balance.currency)}
                </>
              )}
              <br />
              Deposits held:{' '}
              {formatCents(balance.depositsHeldCents, balance.currency)}
            </Alert>
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => setPayOpen(true)}
              disabled={!unitId}
            >
              Add Payment
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={() => handleExportCsv('tenant_ledger')}
            >
              Export CSV
            </Button>
          </Stack>

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Ledger
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Debit</TableCell>
                <TableCell align="right">Credit</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.entries || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary" variant="body2">
                      No ledger entries yet. Create a rent ledger from the lease
                      (with &quot;Post due charges&quot;) or add a payment.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {[...(data.entries || [])]
                .sort(
                  (a, b) =>
                    String(b.entryDate || '').localeCompare(String(a.entryDate || '')) ||
                    String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
                )
                .map((e) => {
                const canVoid =
                  e.entryType === 'payment' &&
                  e.relatedPaymentId &&
                  paymentStatusById[e.relatedPaymentId] !== 'voided';
                const isVoidedPayment =
                  e.relatedPaymentId &&
                  paymentStatusById[e.relatedPaymentId] === 'voided';
                return (
                  <TableRow key={e.id}>
                    <TableCell>{e.entryDate}</TableCell>
                    <TableCell>
                      {e.entryType}
                      {isVoidedPayment ? ' (voided)' : ''}
                    </TableCell>
                    <TableCell>{e.description}</TableCell>
                    <TableCell align="right">
                      {e.debitCents
                        ? formatCents(e.debitCents, e.currency)
                        : ''}
                    </TableCell>
                    <TableCell align="right">
                      {e.creditCents
                        ? formatCents(e.creditCents, e.currency)
                        : ''}
                    </TableCell>
                    <TableCell align="right">
                      {canVoid && (
                        <Button
                          size="small"
                          color="warning"
                          onClick={() => openVoidPayment(e.relatedPaymentId)}
                        >
                          Void
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Schedule
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Due</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...(data.schedules || [])]
                .sort((a, b) => String(a.dueDate || '').localeCompare(String(b.dueDate || '')))
                .slice(0, 24)
                .map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.dueDate}</TableCell>
                  <TableCell>{s.lineType}</TableCell>
                  <TableCell>{s.status}</TableCell>
                  <TableCell align="right">
                    {formatCents(s.amountCents, s.currency)}
                  </TableCell>
                </TableRow>
              ))}
              {(data.schedules || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography color="text.secondary" variant="body2">
                      No schedule — create a rent ledger from an active lease.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Deposits
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Expected</TableCell>
                <TableCell align="right">Collected</TableCell>
                <TableCell align="right">Held</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.deposits || []).map((d) => {
                const held = heldDepositCents(d);
                const remainCollect =
                  Number(d.expectedAmountCents || 0) -
                  Number(d.collectedAmountCents || 0);
                const voided = d.status === 'voided';
                return (
                  <TableRow key={d.id}>
                    <TableCell>{d.depositType}</TableCell>
                    <TableCell>{d.status}</TableCell>
                    <TableCell align="right">
                      {formatCents(d.expectedAmountCents, d.currency)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCents(d.collectedAmountCents, d.currency)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCents(held, d.currency)}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button
                          size="small"
                          disabled={voided || remainCollect <= 0}
                          onClick={() => openDepositAction(d, 'collect')}
                        >
                          Collect
                        </Button>
                        <Button
                          size="small"
                          disabled={voided || held <= 0}
                          onClick={() => openDepositAction(d, 'apply')}
                        >
                          Apply
                        </Button>
                        <Button
                          size="small"
                          disabled={voided || held <= 0}
                          onClick={() => openDepositAction(d, 'refund')}
                        >
                          Refund
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {(data.deposits || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary" variant="body2">
                      No deposits on file.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Receipts
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Number</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Method</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">PDF</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.receipts || []).map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.receiptNumber}</TableCell>
                  <TableCell>{r.paymentDate}</TableCell>
                  <TableCell>{r.methodType}</TableCell>
                  <TableCell align="right">
                    {formatCents(r.amountCents, r.currency)}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() =>
                        handleDownloadReceipt(r.id, r.receiptNumber)
                      }
                    >
                      PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {(data.receipts || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary" variant="body2">
                      No receipts yet — add a payment to generate one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </>
      )}

      <Dialog open={payOpen} onClose={() => !submitting && setPayOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Amount (dollars)"
              type="number"
              value={payForm.amountDollars}
              onChange={(e) =>
                setPayForm((f) => ({ ...f, amountDollars: e.target.value }))
              }
              inputProps={{ min: 0, step: '0.01' }}
              required
              fullWidth
            />
            <TextField
              label="Payment date"
              type="date"
              value={payForm.paymentDate}
              onChange={(e) =>
                setPayForm((f) => ({ ...f, paymentDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="pay-method-label">Method</InputLabel>
              <Select
                labelId="pay-method-label"
                label="Method"
                value={payForm.methodType}
                onChange={(e) =>
                  setPayForm((f) => ({ ...f, methodType: e.target.value }))
                }
              >
                {METHOD_OPTIONS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m.replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Reference #"
              value={payForm.referenceNumber}
              onChange={(e) =>
                setPayForm((f) => ({ ...f, referenceNumber: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Notes"
              value={payForm.notes}
              onChange={(e) =>
                setPayForm((f) => ({ ...f, notes: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePayment}
            disabled={submitting || !payForm.amountDollars}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(depositDialog)}
        onClose={() => !submitting && setDepositDialog(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{depositActionLabel}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {depositDialog?.action === 'apply' && (
              <Alert severity="info">
                Applying a deposit reduces held funds and credits rent due. It
                is not recorded as rent income.
              </Alert>
            )}
            {depositDialog?.action === 'refund' && (
              <Alert severity="info">
                Refund returns held deposit to the tenant. Rent balance is
                unchanged.
              </Alert>
            )}
            <TextField
              label="Amount (dollars)"
              type="number"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDepositDialog(null)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDepositAction}
            disabled={submitting || !depositAmount}
          >
            {submitting ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(voidDialog)}
        onClose={() => !submitting && setVoidDialog(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Void payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Soft-void only — the payment stays on the ledger with a reversal.
              Balance returns to the prior amount.
            </Alert>
            <TextField
              label="Reason"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              required
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidDialog(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleVoidPayment}
            disabled={submitting || !voidReason.trim()}
          >
            {submitting ? 'Voiding…' : 'Void payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}

export default TenantLedgerView;
